import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../../Store/Store';
import ManageTag from '../../Management/ManageTag';
import Body from './Body';
import ProductStatusScreen from './ProductStatusScreen';
import { buildScreenSequence } from './screenSequence';

export default function RoutedBody() {
  const {
    token,
    product,
    event,
    appState,
    backendBaseUrl,
    fetchData,
    isEventMode,
  } = useAppContext();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [screens, setScreens] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const productParam = searchParams.get('product');
    const tokenParam = searchParams.get('token');
    const eventParam = searchParams.get('event');

    // Don't retry if already in a terminal state
    const terminalStates = [
      'not-found',
      'error',
      'no-token',
      'managetag',
      'initial',
    ];
    if (terminalStates.includes(appState)) return;

    if (productParam && !product) {
      fetchData();
    }

    if (tokenParam && !product && appState === 'loading') {
      fetchData();
    }

    if (eventParam && !product && appState === 'loading') {
      fetchData();
    }
  }, [searchParams, product, appState, fetchData]);

  // Stop the local loading spinner on terminal states that never set product/event
  useEffect(() => {
    const terminalStates = ['not-found', 'error', 'no-token'];
    if (terminalStates.includes(appState)) {
      setLoading(false);
    }
  }, [appState]);

  const checkForGeoManagement = () => {
    const urlProduct = searchParams.get('product');

    if (!urlProduct || !product) return false;

    const baseProductCode = urlProduct.split('/')[0];

    if (baseProductCode === product.code && product.type === 'geo') {
      return !urlProduct.includes('/digital-content-pdf');
    }
    return false;
  };

  useEffect(() => {
    if (!product || !event) {
      return;
    }

    const fetchScreensAndRedirect = async () => {
      try {
        const response = await axios.get(`${backendBaseUrl}/event-screens/`, {
          params: { event_id: event.id },
        });
        const rawScreens = response.data.screens as string[];
        // Ingresso per evento: la prima schermata e' il primo screen type per
        // order dell'evento/event-type, non quella del type del primo prodotto.
        const screenList = buildScreenSequence(rawScreens, product, {
          isEventEntry: isEventMode,
        });
        setScreens(screenList);

        if (screenList.length > 0) {
          const firstScreen = screenList[0];
          const currentPath = window.location.pathname;

          if (currentPath === '/') {
            const searchParamsString = searchParams.toString();
            const queryString = searchParamsString
              ? `?${searchParamsString}`
              : '';
            navigate(`/${firstScreen}${queryString}`, { replace: true });
          } else if (currentPath !== `/${firstScreen}`) {
            const searchParamsString = searchParams.toString();
            const queryString = searchParamsString
              ? `?${searchParamsString}`
              : '';
            console.log(
              `Redirecting from direct screen access ${currentPath} to first screen /${firstScreen}`
            );
            navigate(`/${firstScreen}${queryString}`, { replace: true });
          }
        }
      } catch (error) {
        console.error('Failed to fetch screens:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchScreensAndRedirect();
  }, [
    event,
    token,
    product,
    appState,
    backendBaseUrl,
    navigate,
    searchParams,
    isEventMode,
  ]);

  // Aggiungo un useEffect per gestire il cambio di appState
  useEffect(() => {
    // Se appState è managetag, non abbiamo bisogno di loading
    if (appState === 'managetag') {
      setLoading(false);
    }
  }, [appState]);

  if (loading) {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1rem',
          boxSizing: 'border-box',
          textAlign: 'center',
        }}
      >
        Loading...
      </div>
    );
  }

  if (
    appState === 'not-found' ||
    appState === 'error' ||
    appState === 'no-token'
  ) {
    return <ProductStatusScreen state={appState} />;
  }

  if (appState === 'managetag') {
    return <ManageTag />;
  }

  if (checkForGeoManagement()) {
    return <Body />;
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
        boxSizing: 'border-box',
        textAlign: 'center',
      }}
    >
      Initializing...
    </div>
  );
}
